import type Logger from "js/core/logger/Logger";

export function checkIfUpdateBlindlyReplacesSlideRefs(changeSet: any): boolean {
    if (!changeSet?.update || !("slideRefs" in changeSet.update)) return false;

    const origSlideRefs = changeSet.original?.slideRefs;
    const updateSlideRefs = changeSet.update.slideRefs;
    const origIsBlank = origSlideRefs == null ||
        (typeof origSlideRefs === "object" && Object.keys(origSlideRefs).length === 0);
    const updateHasKeys = updateSlideRefs != null &&
        typeof updateSlideRefs === "object" &&
        Object.keys(updateSlideRefs).length > 0;

    return origIsBlank && updateHasKeys;
}

export function checkIfSlideRefsChangeSetIsInconsistent(
    currentAttributes: any,
    newAttrs: any,
    changeSet: any
): boolean {
    // Independently re-derives the post-update slideRefs by applying changeSet.update.slideRefs
    // to currentAttributes.slideRefs, then compares to newAttrs.slideRefs. Returns true when
    // they diverge — which would indicate computeChangeSet (or whatever built changeSet) emitted
    // a result that doesn't match the caller's intent.
    //
    // Limitation: with removeMissingKeys=false (the StorageModel default), computeChangeSet does
    // not emit anything for keys that exist in currentAttributes but are absent in newAttrs.
    // In that case changeSet.update has no slideRefs entry and this check early-returns. A
    // separate check would be needed to catch unintended deletions via missing keys.
    if (!newAttrs || !("slideRefs" in newAttrs)) return false;
    if (!changeSet?.update || !("slideRefs" in changeSet.update)) return false;

    const currentSlideRefs: Record<string, number | null> = currentAttributes?.slideRefs ?? {};
    const newSlideRefs: Record<string, number | null> = newAttrs.slideRefs ?? {};
    const updateSlideRefs: Record<string, number | null> = changeSet.update.slideRefs ?? {};

    // Reconstruct the post-update state: start from current, overlay update (null = delete).
    const reconstructed: Record<string, number> = {};
    for (const [key, value] of Object.entries(currentSlideRefs)) {
        if (value !== null && value !== undefined) {
            reconstructed[key] = value as number;
        }
    }
    for (const [key, value] of Object.entries(updateSlideRefs)) {
        if (value === null) {
            delete reconstructed[key];
        } else {
            reconstructed[key] = value as number;
        }
    }

    // Strip nulls from newSlideRefs for the comparison.
    const expected: Record<string, number> = {};
    for (const [key, value] of Object.entries(newSlideRefs)) {
        if (value !== null && value !== undefined) {
            expected[key] = value as number;
        }
    }

    const reconstructedKeys = Object.keys(reconstructed).sort();
    const expectedKeys = Object.keys(expected).sort();
    if (reconstructedKeys.length !== expectedKeys.length) return true;
    for (let i = 0; i < reconstructedKeys.length; i++) {
        if (reconstructedKeys[i] !== expectedKeys[i]) return true;
        if (reconstructed[reconstructedKeys[i]] !== expected[expectedKeys[i]]) return true;
    }
    return false;
}

export function checkIfUpdateLeavesOnlyOneSlide(currentAttributes: any, newAttrs: any): boolean {
    // StorageModel-side equivalent of checkIfUpdateSetsAllButOneSlideToNull.
    // Detects when an update is about to reduce slideRefs from many visible slides
    // down to a single one — the same destructive intent, but observed before the
    // changeset is computed (so we look at the full before/after state, not the diff).
    if (!newAttrs || !("slideRefs" in newAttrs)) return false;

    const newSlideRefs = newAttrs.slideRefs;
    if (!newSlideRefs || typeof newSlideRefs !== "object") return false;

    const currentSlideRefs = currentAttributes?.slideRefs;
    if (!currentSlideRefs || typeof currentSlideRefs !== "object") return false;

    const newNonNullCount = Object.values(newSlideRefs).filter(v => v !== null).length;
    const currentNonNullCount = Object.values(currentSlideRefs).filter(v => v !== null).length;

    return newNonNullCount === 1 && currentNonNullCount > 2;
}

export function troubleshootingLogs({ id, attributes, attrs, changeSet, options, logger }: {
    id: any;
    attributes: any;
    attrs: any;
    changeSet: any;
    options: any;
    logger: Logger;
}): void {
    const loggerPayload = {
        id: id,
        attributes: Object.fromEntries(Object.entries(attributes).filter(([key]) => key in attrs)),
        attrs: attrs,
        changeSet: changeSet,
        options: options
    };

    try {
        if (checkIfUpdateLeavesOnlyOneSlide(attributes, attrs)) {
            logger.warn("[StorageModel] update() leave only one slide in changeSet",
                { loggerPayload });
        }
    } catch (err) {
        logger.error(err, "[StorageModel] update() storageModelUtils.checkIfUpdateLeavesOnlyOneSlide() threw an error",
            { loggerPayload });
    }
    try {
        if (checkIfUpdateBlindlyReplacesSlideRefs(changeSet)) {
            const err = new Error("[StorageModel] update() blindly replaces slideRefs in changeSet");
            logger.error(err, "[StorageModel] update() blindly replaces slideRefs in changeSet",
                { loggerPayload });
        }
    } catch (err) {
        logger.error(err, "[StorageModel] update() storageModelUtils.checkIfUpdateBlindlyReplacesSlideRefs() threw an error",
            { loggerPayload });
    }
    try {
        if (checkIfSlideRefsChangeSetIsInconsistent(attributes, attrs, changeSet)) {
            const err = new Error("[StorageModel] update() calculates inconsistent changeSet");
            logger.error(err, "[StorageModel] update() calculates inconsistent changeSet",
                { loggerPayload });
        }
    } catch (err) {
        logger.error(err, "[StorageModel] update() storageModelUtils.checkIfSlideRefsChangeSetIsInconsistent() threw an error",
            { loggerPayload });
    }
}
